import React, { FC, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { useFetchOptionGroupsQuery } from "../../../../Domains/OptionGroups/api/optionGroupsApi.ts";
import { ProductOptionGroups } from "./ProductOptionGroups.tsx";
import { Stack } from "@mui/material";
import { ProductAttributes, useLazyFetchProductsJsonQuery } from "../../../../Domains/Products/api/productJsonApi.ts";
import _ from "lodash";
import { RenderOption } from "../../views/OptionGroups/Tabs/AutocompleteProductRenderOption.tsx";
import { AutocompleteProduct } from "@jamezz/react-components";
import { OptionGroupData, ProductData, ProductOptionGroupDataContext } from "./ProductAndOptionGroupContext.tsx";

export type DiscountedProduct = {
  id: number;
  maxCount: number;
  optionGroups?: DiscountedOptionGroup[];
};

export type DiscountedOptionGroup = {
  id: number;
  maxCount: number;
  allOptionsCount: boolean;
  options?: DiscountedProduct[];
};

type DiscountedProductsTableProps = {
  discountedProducts: DiscountedProduct[];
  replace: (products: DiscountedProduct[]) => void;
  onChange: (products: DiscountedProduct[]) => void;
  salesAreaId: number;
};

const extractIdsRecursively = (
  products: DiscountedProduct[],
  productIds: number[] = [],
  optionGroupIds: number[] = []
) => {
  products.forEach((product) => {
    productIds.push(product.id);

    if (product.optionGroups) {
      product.optionGroups.forEach((optionGroup) => {
        optionGroupIds.push(optionGroup.id);

        if (optionGroup.options) {
          extractIdsRecursively(optionGroup.options, productIds, optionGroupIds);
        }
      });
    }
  });

  return {
    productIdsString: productIds.join(","),
    optionGroupIdsString: optionGroupIds.join(","),
  };
};

export const DiscountedProductsTable: FC<DiscountedProductsTableProps> = ({
  discountedProducts,
  replace,
  onChange,
  salesAreaId,
}) => {
  const [allProducts, setAllProducts] = useState<ProductData>({});
  const [allOptionGroups, setAllOptionGroups] = useState<OptionGroupData>({});

  const productIds = useMemo(() => {
    return discountedProducts.map((value) => String(value.id));
  }, [discountedProducts]);

  return (
    <Stack direction={"column"} gap={2} width={1} sx={{ my: 2 }}>
      <AutocompleteProduct<true, ProductAttributes, true>
        multiple={true}
        menuSalesareaId={salesAreaId}
        fetchQuery={useLazyFetchProductsJsonQuery}
        inputFieldProps={{ sx: { width: 1 } }}
        defaultFilters={{
          vestiging_id: { field: "vestiging_id", operator: "=", value: salesAreaId },
          is_option: { field: "is_option", value: false },
        }}
        onFetched={(fetchedData) => {
          setAllProducts(_.merge({}, allProducts, _.keyBy(fetchedData, "id")));
        }}
        onChange={(option) => {
          replace(
            _.map(option, (option) => ({
              id: parseInt(option.id),
              maxCount: 0,
              optionGroups: [],
            }))
          );
        }}
        value={productIds}
        renderOption={(props, option) => <RenderOption option={option} />}
      />
      <ProductOptionGroupDataContext.Provider
        value={{
          productData: allProducts,
          optionGroupData: allOptionGroups,
          setProductData: setAllProducts,
          setOptionGroupData: setAllOptionGroups,
        }}
      >
        <Box sx={{ width: "100%" }}>
          {discountedProducts.map((discountedProduct) => {
            return (
              <ProductOptionGroups
                key={discountedProduct.id}
                expanded={false}
                salesAreaId={salesAreaId}
                discountedProduct={discountedProduct}
                onChange={(changedProduct) => {
                  onChange(
                    discountedProducts.map((product) => {
                      if (product.id === changedProduct.id) {
                        return changedProduct;
                      }

                      return product;
                    })
                  );
                }}
              />
            );
          })}
        </Box>
      </ProductOptionGroupDataContext.Provider>
    </Stack>
  );
};
